import SEOMetadata from '@/components/SEOMetadata';
import React, { Suspense, useState } from 'react';
import StyledJobs from './styles/JobsPageStyles';
import { Building2, MapPin } from 'lucide-react';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

const jobs = [
  {
    title: 'Senior Software Engineer (Backend)',
    department: 'engineering',
    location: 'Remote',
    description:
      'We are seeking an experienced Backend Software Engineer to design, develop, and maintain robust and scalable server-side applications for our financial data API. Strong proficiency in Node.js, Python, or Go, and experience with microservices architecture is required.',
  },
  {
    title: 'Frontend Developer',
    department: 'engineering',
    location: 'Remote',
    description:
      'Join our team as a Frontend Developer specializing in React. You will be responsible for building responsive and user-friendly web interfaces for our stock market data platform, collaborating with designers and product managers to deliver engaging experiences.',
  },
  {
    title: 'DevOps Engineer',
    department: 'engineering',
    location: 'Remote',
    description:
      'We are looking for a skilled DevOps Engineer to manage our infrastructure, automate deployment pipelines, and ensure the reliability and scalability of our financial data platform. Experience with AWS/GCP/Azure, Kubernetes, and CI/CD tools is essential.',
  },
  {
    title: 'Product Designer (UI/UX)',
    department: 'design',
    location: 'Remote',
    description:
      'As a Product Designer, you will lead the UI/UX design for new features and products, from concept to implementation. You should have a strong portfolio demonstrating your ability to create intuitive and aesthetically pleasing digital experiences.',
  },
  {
    title: 'Data Scientist',
    department: 'data',
    location: 'Remote',
    description:
      'We are hiring a Data Scientist to analyze complex financial datasets, build predictive models, and provide actionable insights for quantitative finance. Proficiency in Python or R, SQL, and machine learning techniques is a must.',
  },
  {
    title: 'Technical Writer',
    department: 'content',
    location: 'Remote',
    description:
      'We need a Technical Writer to create clear, concise, and comprehensive documentation for our financial data API and developer tools. Experience explaining complex technical concepts to a technical audience is required.',
  },
  {
    title: 'Sales Development Representative',
    department: 'sales',
    location: 'Remote',
    description:
      'We are looking for a motivated Sales Development Representative to identify and qualify new business opportunities, nurturing leads and contributing to our sales pipeline.',
  },
];

const JobsPage: React.FC = () => {
  useIdlePrefetch([loadFooter]);
  const [searchTerm, setSearchTerm] = useState('');
  const [department, setDepartment] = useState('');

  const filteredJobs = jobs.filter((job) => {
    const searchTermMatch =
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase());
    const departmentMatch = department ? job.department === department : true;
    return searchTermMatch && departmentMatch;
  });

  return (
    <>
      <SEOMetadata
        title="Careers – Join the AxionQuant Team"
        description="Join AxionQuant and help build the future of financial data APIs. We're hiring engineers, data scientists, and product managers."
        canonicalUrl="https://axionquant.com/jobs"
      />
      <StyledJobs>
        <div className="fold">
          <div aria-hidden="true">
            <video autoPlay loop muted playsInline controls={false} fetchPriority="high" src="https://cdn.studio14a.com/axion/uc/3.mp4"/>
          </div>
          <div className="text">
            <h1 className="serif">Careers</h1>
            <span>Join us in building the future of financial data.</span>
          </div>
        </div>

        <div className="jobs-container">
          <div className="filter-bar">
            <input
              type="text"
              placeholder="Search by title or description…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="filter-options">
              <select aria-label="Filter by department" value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">All Departments</option>
                <option value="engineering">Engineering</option>
                <option value="design">Design</option>
                <option value="data">Data</option>
                <option value="content">Content</option>
                <option value="sales">Sales</option>
              </select>
            </div>
          </div>

          <p className="results-count">
            {filteredJobs.length} open {filteredJobs.length === 1 ? 'position' : 'positions'}
          </p>

          <div className="job-listings">
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job, index) => (
                <div className="job" key={index}>
                  <div className="job-info">
                    <h2 className="sans">{job.title}</h2>
                    <div className="job-meta">
                      <div>
                        <Building2 size={14} />
                        <span>{job.department}</span>
                      </div>
                      <div>
                        <MapPin size={14} />
                        <span>{job.location}</span>
                      </div>
                    </div>
                    <p className="description">{job.description}</p>
                  </div>
                  <div className="job-action">
                    <a
                      href={`mailto:jobs@axionquant.com?subject=Application for ${job.title}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <button>Apply</button>
                    </a>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <h2 className="sans">No positions found</h2>
                <p>Try adjusting your search or department filter.</p>
              </div>
            )}
          </div>
        </div>

        <Suspense fallback={null}>
        <Footer />
        </Suspense>
      </StyledJobs>
    </>
  );
};

export default JobsPage;
